import React, { useState, useEffect, useCallback } from 'react';
import EarningsIngestModal from './EarningsIngestModal';
import EarningsEditModal from './EarningsEditModal';
import EarningsCharts from './EarningsCharts';
import CustomEarningsChart from './CustomEarningsChart';
import { reviewsService } from '../services/reviewsService';

/**
 * Derive a quarter label like "Q4 2025" from a period_end_date string.
 */
function getQuarterLabel(periodEndDate) {
  if (!periodEndDate) return 'Unknown Period';
  const d = new Date(periodEndDate + 'T00:00:00');
  if (isNaN(d.getTime())) return 'Unknown Period';
  const quarter = Math.ceil((d.getMonth() + 1) / 3);
  return `Q${quarter} ${d.getFullYear()}`;
}

const CompanyEarningsTab = ({ companyName, companyId, modalOpen, setModalOpen, onCountChange }) => {
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const isModalOpen = modalOpen !== undefined ? modalOpen : internalModalOpen;
  const openModal = setModalOpen || setInternalModalOpen;
  const [ingestedRecords, setIngestedRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [expandedRecords, setExpandedRecords] = useState({});
  const [editingRecord, setEditingRecord] = useState(null); // index or null
  const [renamingRecord, setRenamingRecord] = useState(null); // index or null
  const [renameValue, setRenameValue] = useState('');
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null); // index or null
  const [deleting, setDeleting] = useState(false);

  const toggleRecord = (rIdx) =>
    setExpandedRecords((prev) => ({ ...prev, [rIdx]: !prev[rIdx] }));

  const loadRecords = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const records = await reviewsService.getEarnings(companyId);
      // Map API shape and sort by period_end_date descending
      const mapped = records.map((r) => ({
        fields: r.sections,
        fileNames: [],
        timestamp: r.ingested_at || r.period_end_date,
        period_end_date: r.period_end_date,
      }));
      mapped.sort((a, b) => (b.period_end_date || '').localeCompare(a.period_end_date || ''));
      setIngestedRecords(mapped);
      if (onCountChange) onCountChange(records.length);
    } catch (_) {
      // Non-critical — leave list empty
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleIngestComplete = async (fields, fileNames) => {
    setSaveError('');
    // Determine period_end_date from _meta section if present
    const meta = fields._meta || fields._root || {};
    const periodEndDate =
      (typeof meta === 'object' ? meta.period_end_date : null) ||
      new Date().toISOString().slice(0, 10);

    try {
      await reviewsService.saveEarnings(companyId, periodEndDate, fields);
      // Refresh from server so we have canonical data
      await loadRecords();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      console.error('Earnings save failed:', e?.response?.status, detail, e);
      setSaveError(`Save failed (${e?.response?.status || 'network'}): ${detail} — record kept locally only.`);
      // Still show in UI even if save failed
      setIngestedRecords((prev) => [
        { fields, fileNames, timestamp: new Date().toISOString(), period_end_date: periodEndDate },
        ...prev,
      ]);
    }
  };

  const handleDelete = async (rIdx) => {
    const record = ingestedRecords[rIdx];
    if (!record) return;
    setDeleting(true);
    try {
      await reviewsService.deleteEarnings(companyId, record.period_end_date);
      setConfirmDeleteIdx(null);
      await loadRecords();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      setSaveError(`Delete failed: ${detail}`);
      setConfirmDeleteIdx(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleEditSave = async (cId, periodEndDate, sections) => {
    await reviewsService.saveEarnings(cId, periodEndDate, sections);
    await loadRecords();
  };

  const handleRenameSave = async (rIdx) => {
    const record = ingestedRecords[rIdx];
    if (!record) return;
    const sections = { ...record.fields };
    const meta = { ...(sections._meta || {}) };
    const trimmed = renameValue.trim();
    if (trimmed) {
      meta.display_name = trimmed;
    } else {
      delete meta.display_name;
    }
    sections._meta = meta;
    await reviewsService.saveEarnings(companyId, record.period_end_date, sections);
    setRenamingRecord(null);
    setRenameValue('');
    await loadRecords();
  };

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        padding: '12px 0',
      }}
    >

      {/* Content area */}
      {saveError && (
        <div style={{ fontSize: '0.72rem', color: '#e05c5c', marginBottom: 6 }}>{saveError}</div>
      )}
      {ingestedRecords.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#7a6a40',
          }}
        >
          <div style={{ fontSize: '2rem', marginBottom: 12, opacity: 0.4 }}>$</div>
          <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#c9a84c', marginBottom: 6 }}>
            No Earnings Data
          </div>
          <div style={{ fontSize: '0.8rem', color: '#7a6a40' }}>
            Use the button above to ingest earnings documents for {companyName}.
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <EarningsCharts records={ingestedRecords} />
          <CustomEarningsChart records={ingestedRecords} companyId={companyId} />
          {ingestedRecords.map((record, rIdx) => {
            const isExpanded = !!expandedRecords[rIdx];
            return (
            <div
              key={rIdx}
              style={{
                backgroundColor: '#111008',
                border: '1px solid #3a2e00',
                borderRadius: 8,
                marginBottom: 12,
                overflow: 'hidden',
              }}
            >
              {/* Record header — click to expand/collapse */}
              <button
                type="button"
                onClick={() => toggleRecord(rIdx)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 14px',
                  borderBottom: isExpanded ? '1px solid #3a2e00' : 'none',
                  backgroundColor: '#0c0b02',
                  border: 'none',
                  borderBottom: isExpanded ? '1px solid #3a2e00' : 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <i
                    className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}
                    style={{ color: '#7a6a40', fontSize: '0.75rem', flexShrink: 0 }}
                  />
                  {renamingRecord === rIdx ? (
                    <span className="d-flex align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSave(rIdx); if (e.key === 'Escape') { setRenamingRecord(null); setRenameValue(''); } }}
                        placeholder={getQuarterLabel(record.period_end_date)}
                        style={{
                          background: '#0a0900',
                          border: '1px solid #3a2e00',
                          borderRadius: 3,
                          color: '#f0c040',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          padding: '2px 6px',
                          width: 160,
                          outline: 'none',
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleRenameSave(rIdx); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4caf50', fontSize: '0.8rem', padding: '0 2px' }}
                        title="Save"
                      >
                        <i className="bi bi-check-lg" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setRenamingRecord(null); setRenameValue(''); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e05c5c', fontSize: '0.8rem', padding: '0 2px' }}
                        title="Cancel"
                      >
                        <i className="bi bi-x-lg" />
                      </button>
                    </span>
                  ) : (
                    <>
                      <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#f0c040', marginRight: 4 }}>
                        {(record.fields._meta && record.fields._meta.display_name) || getQuarterLabel(record.period_end_date)}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenameValue((record.fields._meta && record.fields._meta.display_name) || '');
                          setRenamingRecord(rIdx);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7a6a40', fontSize: '0.68rem', padding: '0 2px' }}
                        title="Rename"
                      >
                        <i className="bi bi-pencil" />
                      </button>
                    </>
                  )}
                  <span style={{ fontSize: '0.7rem', color: '#7a6a40' }}>
                    {record.period_end_date}
                  </span>
                </div>
                <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0, marginLeft: 8 }}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setEditingRecord(rIdx); }}
                    style={{
                      background: 'none',
                      border: '1px solid #3a2e00',
                      borderRadius: 4,
                      cursor: 'pointer',
                      color: '#c9a84c',
                      fontSize: '0.65rem',
                      padding: '3px 8px',
                    }}
                    title="Edit record"
                  >
                    <i className="bi bi-pencil-square me-1" />
                    Edit
                  </button>
                  {confirmDeleteIdx === rIdx ? (
                    <span className="d-flex align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <span style={{ fontSize: '0.65rem', color: '#e05c5c' }}>Delete?</span>
                      <button
                        type="button"
                        disabled={deleting}
                        onClick={(e) => { e.stopPropagation(); handleDelete(rIdx); }}
                        style={{
                          background: 'none',
                          border: '1px solid #8b1a1a',
                          borderRadius: 4,
                          cursor: 'pointer',
                          color: '#e05c5c',
                          fontSize: '0.65rem',
                          padding: '3px 8px',
                        }}
                      >
                        {deleting ? '…' : 'Yes'}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteIdx(null); }}
                        style={{
                          background: 'none',
                          border: '1px solid #3a2e00',
                          borderRadius: 4,
                          cursor: 'pointer',
                          color: '#7a6a40',
                          fontSize: '0.65rem',
                          padding: '3px 8px',
                        }}
                      >
                        No
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteIdx(rIdx); }}
                      style={{
                        background: 'none',
                        border: '1px solid #3a2e00',
                        borderRadius: 4,
                        cursor: 'pointer',
                        color: '#e05c5c',
                        fontSize: '0.65rem',
                        padding: '3px 8px',
                      }}
                      title="Delete record"
                    >
                      <i className="bi bi-trash" />
                    </button>
                  )}
                </div>
              </button>

              {/* Fields — grouped by section, only shown when expanded */}
              {isExpanded && Object.entries(record.fields).map(([sectionKey, sectionVal]) => {
                const isSection = sectionVal && typeof sectionVal === 'object' && !Array.isArray(sectionVal);
                const entries = isSection ? Object.entries(sectionVal) : [[sectionKey, sectionVal]];
                const sectionLabel = sectionKey === '_root'
                  ? null
                  : sectionKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                return (
                  <div key={sectionKey}>
                    {sectionLabel && (
                      <div style={{
                        padding: '5px 14px',
                        backgroundColor: '#0f0c02',
                        borderBottom: '1px solid #3a2e00',
                        borderTop: '1px solid #3a2e00',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: '#f0c040',
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                      }}>
                        {sectionLabel}
                      </div>
                    )}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                      gap: 1,
                      backgroundColor: '#3a2e00',
                    }}>
                      {entries.map(([key, value], idx) => (
                        <div
                          key={idx}
                          style={{
                            backgroundColor: idx % 2 === 0 ? '#111008' : '#0c0b02',
                            padding: '7px 12px',
                          }}
                        >
                          <div style={{ fontSize: '0.65rem', color: '#9a8a60', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                            {key}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#fff', fontWeight: 500 }}>
                            {value !== null && value !== '' && value !== undefined
                              ? String(value)
                              : <span style={{ color: '#7a6a40' }}>—</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })}
        </div>
      )}

      <EarningsIngestModal
        isOpen={isModalOpen}
        onClose={() => openModal(false)}
        companyName={companyName}
        companyId={companyId}
        onIngestComplete={handleIngestComplete}
      />
      <EarningsEditModal
        isOpen={editingRecord !== null}
        onClose={() => setEditingRecord(null)}
        record={editingRecord !== null ? ingestedRecords[editingRecord] : null}
        companyId={companyId}
        onSave={handleEditSave}
      />
    </div>
  );
};

export default CompanyEarningsTab;
